import { directSupabase } from '../../lib/supabaseClient';
import { emitActivityEventBackground } from '../../services/activityEvents.service';
import type {
  ProjectSupplierReference,
  ProjectSupplierReferenceInput,
} from './supplierReferences.types';

function table() {
  return (directSupabase as any).from('project_supplier_references');
}

function asRow(row: Record<string, unknown>): ProjectSupplierReference {
  return {
    id: String(row.id || ''),
    project_id: String(row.project_id || ''),
    supplier_id: row.supplier_id ? String(row.supplier_id) : null,
    supplier_name: row.supplier_name ? String(row.supplier_name) : null,
    reference: String(row.reference || '').trim(),
    notes: row.notes ? String(row.notes) : null,
    created_at: String(row.created_at || ''),
    updated_at: String(row.updated_at || ''),
    created_by: row.created_by ? String(row.created_by) : null,
  };
}

function normalizeInput(input: ProjectSupplierReferenceInput): {
  supplier_id: string | null;
  supplier_name: string | null;
  reference: string;
  notes: string | null;
} {
  const reference = String(input.reference || '').trim();
  if (!reference) throw new Error('Supplier reference is required');
  const supplierId = String(input.supplier_id || '').trim() || null;
  const supplierName = String(input.supplier_name || '').trim() || null;
  const notes = String(input.notes || '').trim() || null;
  return {
    supplier_id: supplierId,
    supplier_name: supplierName,
    reference,
    notes,
  };
}

export async function listProjectSupplierReferences(
  projectId: string,
): Promise<ProjectSupplierReference[]> {
  const id = String(projectId || '').trim();
  if (!id) return [];
  const { data, error } = await table()
    .select('*')
    .eq('project_id', id)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return ((data || []) as Record<string, unknown>[]).map(asRow);
}

export async function createProjectSupplierReference(
  projectId: string,
  input: ProjectSupplierReferenceInput,
): Promise<ProjectSupplierReference> {
  const id = String(projectId || '').trim();
  if (!id) throw new Error('Project is required');
  const payload = normalizeInput(input);
  const { data: sessionData } = await directSupabase.auth.getSession();
  const createdBy = sessionData.session?.user?.id || null;
  const { data, error } = await table()
    .insert({
      project_id: id,
      ...payload,
      created_by: createdBy,
    })
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  const row = asRow(data as Record<string, unknown>);
  emitActivityEventBackground({
    action: 'supplier.reference_added',
    entityType: 'supplier_reference',
    entityId: row.id,
    projectId: id,
    actorId: createdBy,
    label: row.supplier_name
      ? `Supplier reference added (${row.supplier_name})`
      : 'Supplier reference added',
    metadata: {
      reference: row.reference,
      supplierId: row.supplier_id,
      supplierName: row.supplier_name,
    },
  });
  return row;
}

/** Copies a workspace's supplier references onto another workspace, keeping who added them and when. */
export async function copyProjectSupplierReferences(
  sourceProjectId: string,
  targetProjectId: string,
): Promise<number> {
  const { data, error } = await table()
    .select('supplier_id, supplier_name, reference, notes, created_at, updated_at, created_by')
    .eq('project_id', sourceProjectId)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  const rows = (data || []) as Record<string, unknown>[];
  if (!rows.length) return 0;
  const { error: insertError } = await table().insert(rows.map((row) => ({
    project_id: targetProjectId,
    supplier_id: row.supplier_id || null,
    supplier_name: row.supplier_name || null,
    reference: row.reference,
    notes: row.notes || null,
    created_at: row.created_at,
    updated_at: row.updated_at || row.created_at,
    created_by: row.created_by || null,
  })));
  if (insertError) throw new Error(insertError.message);
  return rows.length;
}

export async function updateProjectSupplierReference(
  id: string,
  input: ProjectSupplierReferenceInput,
): Promise<ProjectSupplierReference> {
  const rowId = String(id || '').trim();
  if (!rowId) throw new Error('Reference id is required');
  const payload = normalizeInput(input);
  const { data, error } = await table()
    .update(payload)
    .eq('id', rowId)
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  return asRow(data as Record<string, unknown>);
}

export async function deleteProjectSupplierReference(id: string): Promise<void> {
  const rowId = String(id || '').trim();
  if (!rowId) throw new Error('Reference id is required');
  const { error } = await table().delete().eq('id', rowId);
  if (error) throw new Error(error.message);
}
